import React, { useMemo } from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import { CollapsableContainer } from 'design-system/CollapsableContainer';
import Text from 'design-system/Text';
import ToolTip from 'design-system/ToolTip';
import { selectIsGBUser } from 'reducers/selectors';
import { useAppSelector } from 'store/hooks';
import { calculateTotalBalance } from 'utils/accounts';
import { sortAccounts } from 'utils/api/sortAccounts';
import { rem } from 'design-system/values';
import { IconFatPlus } from 'design-system/icons';
import { Spacer } from 'design-system';
import useColors from 'hooks/useColors';
import RowAccount from '../RowAccount';
import RowEmmaInvestAccount from '../RowEmmaInvestAccount';
import { keyExtractor } from './util';
import { isWeb } from '../../../../constants';
import RowPot from '../RowPot';
import UnopenedInvestmentAccountItem from '../UnopenedInvestmentAccountItem';
import UnopenedPotItem from '../UnopenedPotItem';
const AccountsSectionCollapsible = ({ title, data, withTotal, total, color, type, firstCTA, secondCTA, isCollapsedByDefault = true, reverse, rightContent, blur, onRowPress, onRowLongPress, onOpenLogins, onOpenUpgrade, onAddAccount, openAlternative, onCollapsedChange, }) => {
    const userIsGB = useAppSelector(selectIsGBUser);
    const sorted = useMemo(() => sortAccounts(data, reverse), [data, reverse]);
    const calculatedTotal = useMemo(() => {
        if (total) {
            return total;
        }
        if (withTotal) {
            return calculateTotalBalance(data);
        }
        return undefined;
    }, [total, withTotal, data]);
    const colors = useColors();
    const flatList = useMemo(() => (<>
        {sorted.map((item) => {
            if ('investAccountName' in item) {
                return <UnopenedInvestmentAccountItem data={item} key={item.id}/>;
            }
            if ('productName' in item) {
                return <UnopenedPotItem data={item} key={item.id}/>;
            }
            if ('currency' in item) {
                return (<RowAccount key={keyExtractor(item)} data={item} onOpenLogins={onOpenLogins} onRowLongPressed={onRowLongPress} onOpenUpgrade={onOpenUpgrade} onPress={onRowPress} style={styles.row} onCard showGoal/>);
            }
            if ('isEmmaInvestmentAccount' in item) {
                return (<RowEmmaInvestAccount onCard data={item} key={item.id} style={styles.row}/>);
            }
            return <RowPot data={item} key={item.id}/>;
        })}
        {!isWeb && onAddAccount && data?.length ? (<TouchableOpacity style={styles.addMoreContainer} onPress={() => onAddAccount(type)}>
            <View style={styles.addMoreTextContainer}>
              <IconFatPlus color={colors.elements.brand} width={16} height={16}/>
              <Spacer w={4}/>
              <Text Text-16 Primary centered>
                Add more
              </Text>
            </View>
          </TouchableOpacity>) : undefined}
      </>), [
        sorted,
        onAddAccount,
        data?.length,
        onOpenLogins,
        onRowLongPress,
        onOpenUpgrade,
        onRowPress,
        type,
        colors.elements.brand,
    ]);
    const titleBottomContent = useMemo(() => {
        if (data?.length || isWeb) {
            return null;
        }
        if (firstCTA && onAddAccount) {
            const elements = [
                <TouchableOpacity key="firstCTA" onPress={() => {
                        onAddAccount(type);
                    }}>
          <Text TextThin-14 Primary>
            {firstCTA}
          </Text>
        </TouchableOpacity>,
            ];
            if (secondCTA && openAlternative && userIsGB) {
                elements.push(<Text key="or" TextThin-14 Gray>
            {' or '}
          </Text>);
                elements.push(<TouchableOpacity key="secondCTA" onPress={() => {
                        openAlternative(type);
                    }}>
            <Text TextThin-14 Primary>
              {secondCTA}
            </Text>
          </TouchableOpacity>);
            }
            return <View style={styles.callToActionRow}>{elements}</View>;
        }
        return null;
    }, [
        userIsGB,
        data,
        type,
        firstCTA,
        secondCTA,
        onAddAccount,
        openAlternative,
    ]);
    if ((!data || data.length <= 0) && !(onAddAccount && firstCTA)) {
        return null;
    }
    if (calculatedTotal && color) {
        return (<CollapsableContainer style={styles.container} isCollapsedByDefault={isCollapsedByDefault} title={title} amount={calculatedTotal} color={color} onCollapsedChange={onCollapsedChange} titleBottomContent={titleBottomContent} rightContent={rightContent} hasData={!!sorted.length} blur={blur}>
        {flatList}
      </CollapsableContainer>);
    }
    const isHidden = title.startsWith('Hidden');
    return (<CollapsableContainer style={styles.container} isCollapsedByDefault={isCollapsedByDefault} title={title} hasData={!!sorted.length} onCollapsedChange={onCollapsedChange} titleContent={isWeb ? undefined : (<ToolTip small offset={3} title={isHidden
                ? 'What are hidden accounts?'
                : 'What are closed accounts?'} subtitle={isHidden
                ? 'Hidden account keep syncing but they aren’t included in your analytics. While they remain connected, all transactions on these accounts will be hidden.'
                : 'Closed accounts don’t sync anymore, but their data shows across the app. You can move them here once you have closed your account with a bank, so you don’t lose your data.'}/>)} rightContent={rightContent} titleBottomContent={titleBottomContent}>
      {flatList}
    </CollapsableContainer>);
};
const styles = StyleSheet.create({
    container: {
        marginHorizontal: rem(16),
        marginBottom: rem(16),
    },
    row: {
        paddingHorizontal: rem(16),
        ...(isWeb && {
            paddingHorizontal: rem(8),
        }),
    },
    addMoreContainer: {
        padding: rem(16),
    },
    callToActionRow: {
        flexDirection: 'row',
    },
    addMoreTextContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
    },
});
export default AccountsSectionCollapsible;
